import { TopBarInfoLayouts } from 'molecules/InfoBar/InfoBar';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getLocalStorage, setLocalStorage } from 'src/functions/localStorageHandler';
import { FlattenSimpleInterpolation } from 'styled-components';
import { ColorModeBgPropKeys, ColorModeFgPropKeys } from 'styles/colors';

interface TopInfoBarControllerContextData {
  visible?: boolean;
  hide?: () => void;
  dismissible?: boolean;
  dismissButtonColor?: ColorModeBgPropKeys | ColorModeFgPropKeys;
  backgroundColor?: ColorModeBgPropKeys | ColorModeFgPropKeys;
  customStyles?: FlattenSimpleInterpolation;
  dismissKey?: string;
  timeToAutoDismiss?: number;
  contentLayout?: TopBarInfoLayouts;
}

interface TopInfoBarControllerProviderProps {
  children: React.ReactNode;
  visible?: boolean;
  dismissible?: boolean;
  dismissKey?: string;
  dismissButtonColor?: ColorModeBgPropKeys | ColorModeFgPropKeys;
  backgroundColor?: ColorModeBgPropKeys | ColorModeFgPropKeys;
  customStyles?: FlattenSimpleInterpolation;
  contentLayout?: TopBarInfoLayouts;
  timeToAutoDismiss?: number;
  onHide?: () => void;
}

const TopInfoBarControllerContext = createContext<TopInfoBarControllerContextData | null>(null);

const TopInfoBarControllerProvider: React.FC<TopInfoBarControllerProviderProps> = ({
  children,
  visible = true,
  dismissible,
  dismissKey,
  timeToAutoDismiss,
  contentLayout,
  onHide,
  dismissButtonColor,
  backgroundColor,
  customStyles,
}) => {
  const getStorageKey = (key: string) => `topInfoBar@${key}`;

  const getValueFromStorage = (key: string) => getLocalStorage(getStorageKey(key));
  const setValueToStorage = (key: string, value: string) => setLocalStorage(getStorageKey(key), value);

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!visible) {
      setIsVisible(false);
      return;
    }

    if (dismissKey) {
      setIsVisible(!getValueFromStorage(getStorageKey(dismissKey)));
      return;
    }

    setIsVisible(true);
  }, [visible, dismissKey]);

  const handleHide = useCallback(() => {
    if (dismissKey) {
      setValueToStorage(getStorageKey(dismissKey), 'true');
    }

    setIsVisible(false);
    onHide?.();
  }, [onHide, dismissKey]);

  useEffect(() => {
    if (timeToAutoDismiss && isVisible) {
      const timeout = setTimeout(handleHide, timeToAutoDismiss);

      return () => clearTimeout(timeout);
    }
  }, [isVisible, timeToAutoDismiss, handleHide]);

  return (
    <TopInfoBarControllerContext.Provider
      value={{
        visible: isVisible,
        dismissible,
        backgroundColor,
        dismissButtonColor,
        customStyles,
        contentLayout,
        hide: handleHide,
      }}
    >
      {children}
    </TopInfoBarControllerContext.Provider>
  );
};

const useTopInfoBarController = (): TopInfoBarControllerContextData => {
  const context = useContext(TopInfoBarControllerContext) ?? {};

  return context;
};

export { useTopInfoBarController, TopInfoBarControllerProvider };
export type { TopInfoBarControllerProviderProps };
